import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Heart, Loader2, Gift, CheckCircle2 } from 'lucide-react';
import { api } from '@/lib/api';

export default function SupportThankYou() {
    const [params] = useSearchParams();
    const stripeSession = params.get('session_id');
    const paypalOrder = params.get('token'); // PayPal appends ?token=ORDER_ID on return
    const sessionId = stripeSession || paypalOrder;
    const isPaypal = !stripeSession && !!paypalOrder;
    const [state, setState] = useState('checking');
    const [info, setInfo] = useState(null);
    const attempts = useRef(0);
    const [giftEmail, setGiftEmail] = useState('');
    const [giftName, setGiftName] = useState('');
    const [giftState, setGiftState] = useState('idle'); // idle | sending | sent | error
    const [giftErr, setGiftErr] = useState(null);

    useEffect(() => {
        if (!sessionId) { setState('error'); return; }
        let stopped = false;
        const poll = async () => {
            if (stopped) return;
            attempts.current += 1;
            try {
                const { data } = isPaypal
                    ? await api.post('/support/paypal/capture', { order_id: sessionId })
                    : await api.get(`/support/status/${sessionId}`);
                if (stopped) return;
                if (data.status === 'complete') { setInfo(data); setState('complete'); return; }
                if (data.status === 'expired') { setState('error'); return; }
            } catch {}
            if (attempts.current >= 10) { setState('error'); return; }
            setTimeout(poll, 2500);
        };
        poll();
        return () => { stopped = true; };
    }, [sessionId, isPaypal]);

    const sendGift = async () => {
        setGiftErr(null);
        if (!giftEmail.includes('@')) { setGiftErr('Please enter a valid email address.'); return; }
        setGiftState('sending');
        try {
            await api.post('/support/gift-claim', {
                session_id: sessionId, recipient_email: giftEmail, recipient_name: giftName,
            });
            setGiftState('sent');
        } catch (e) {
            setGiftErr(e?.response?.data?.detail || 'Could not send the gift, please try again.');
            setGiftState('error');
        }
    };

    return (
        <main className="max-w-xl mx-auto px-6 py-24" data-testid="page-support-thankyou">
            {state === 'checking' && (
                <div className="text-center"><Loader2 size={40} className="mx-auto animate-spin text-lq-purple-500" />
                <p className="mt-4 text-lq-ink-700">Confirming your donation&hellip;</p></div>
            )}
            {state === 'complete' && (
                <div data-testid="support-thankyou-success">
                    <div className="text-center">
                        <Heart size={48} className="mx-auto text-lq-peach-500" />
                        <h1 className="mt-6 font-display text-4xl font-semibold text-lq-ink-black">Thank you!</h1>
                        <p className="mt-3 text-lq-ink-700">
                            Your {info?.amount_cents ? `£${(info.amount_cents / 100).toFixed(2).replace(/\.00$/, '')} ` : ''}donation goes straight into keeping the puzzles,
                            audio and new languages coming. From all of us — <em>tatenda, e ṣeun, asante, merci!</em>
                        </p>
                    </div>

                    {(info?.gift_eligible || giftState === 'sent') && (
                        <div className="mt-10 bg-white border border-lq-purple-300 rounded-3xl p-6" data-testid="support-gift-claim-card">
                            {giftState !== 'sent' ? (
                                <>
                                    <div className="flex items-start gap-3">
                                        <Gift size={22} className="mt-1 shrink-0 text-lq-purple-700" />
                                        <div>
                                            <h2 className="font-display text-xl font-semibold text-lq-ink-900">Your donation includes a gift</h2>
                                            <p className="mt-1 text-sm text-lq-ink-700">
                                                Because you donated &pound;45 or more, you can gift a <strong>full year of paid access
                                                ({(info?.gift_tier || 'explorer').charAt(0).toUpperCase() + (info?.gift_tier || 'explorer').slice(1)} plan)</strong> to
                                                anyone. Enter their email and we&rsquo;ll deliver it with a redemption code.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-4 space-y-3">
                                        <input type="text" placeholder="Recipient's name (optional)" value={giftName}
                                            onChange={(e) => setGiftName(e.target.value)}
                                            className="w-full border border-lq-ink-100 rounded-xl px-4 py-2.5 focus:outline-none focus:border-lq-purple-500"
                                            data-testid="gift-recipient-name" />
                                        <input type="email" placeholder="Recipient's email address" value={giftEmail}
                                            onChange={(e) => setGiftEmail(e.target.value)}
                                            className="w-full border border-lq-ink-100 rounded-xl px-4 py-2.5 focus:outline-none focus:border-lq-purple-500"
                                            data-testid="gift-recipient-email" />
                                        {giftErr && <p className="text-sm text-lq-error" data-testid="gift-claim-error">{giftErr}</p>}
                                        <button onClick={sendGift} disabled={giftState === 'sending'}
                                            className="w-full inline-flex items-center justify-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold py-3 rounded-full disabled:opacity-60"
                                            data-testid="gift-claim-submit">
                                            {giftState === 'sending' ? <><Loader2 size={16} className="animate-spin"/> Sending&hellip;</> : <><Gift size={16}/> Send the gift</>}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center" data-testid="gift-claim-success">
                                    <CheckCircle2 size={40} className="mx-auto text-lq-success" />
                                    <h2 className="mt-3 font-display text-xl font-semibold text-lq-ink-900">Gift on its way!</h2>
                                    <p className="mt-2 text-sm text-lq-ink-700">
                                        {giftEmail} will receive a 12-month gift code with instructions to redeem it. Thank you for paying it forward.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="mt-8 text-center">
                        <Link to="/" className="inline-block px-6 py-3 rounded-full bg-lq-ink-900 hover:bg-lq-ink-700 text-white font-semibold" data-testid="support-thankyou-home">Back to the puzzles</Link>
                    </div>
                </div>
            )}
            {state === 'error' && (
                <div className="text-center" data-testid="support-thankyou-pending">
                    <h1 className="font-display text-3xl font-semibold text-lq-ink-black">Almost there</h1>
                    <p className="mt-3 text-lq-ink-700">We couldn&rsquo;t confirm the payment yet. If your card was charged, it has been received — thank you! Otherwise you can <Link to="/support" className="text-lq-purple-700 underline">try again</Link>.</p>
                </div>
            )}
        </main>
    );
}
